import { ReactElement, useCallback, useMemo, memo } from 'react';
import { CircularProgress } from '@mui/material';
import {
  Delete as DeleteIcon,
  LinkOff as UnLinkIcon,
  ChangeCircle as ChangeStateIcon,
  ManageAccounts as ModifyAssignmentIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClone as DuplicateIcon,
  faBarcode as BardCodeIcon,
  faRoute as RouteIcon,
} from '@fortawesome/free-solid-svg-icons';
import { isNull } from 'lodash';

import { CollectItemStateTypes, PlanFeaturesTypes } from '~globals/types/enums';
import { useAppSelector } from '~hooks/useAppSelector';
import { selectAuth } from '~store/slices/auth';
import { ItemExtended } from '~services/item/types';
import { validatePartnerItemCollectStateType } from '~utils/partnerItem';
import { isOnlyReadUser, isOnlyCreatorOrder } from '~utils/user';
// import { isCurrentLoggedCompany } from '~utils/partnerCompany';
import { getFeatureAvailableToDate } from '~utils/feature';
import { getUrlTrackingOrder, openUrlInNewTab } from '~utils/commons';
// import { isOnAgendaOrder } from '~utils/order';
// import { replaceParamsPath } from '~utils/router';
import { PATHS } from '~constants/paths';
import MenuActionsDataTable from '~components/MenuActionsDataTable';
// import { ShowIcon } from '~components/MenuActionsDataTable/styles';

import { useDashboardVariantV2CarrierContentInWarehouseTabContext } from '../../Context';
import { ITEM_KEY_ID } from '../../utils';

export interface DashboardVariantV2CarrierContentInWarehouseTabDataTableActionsMenuProps {
  data: ItemExtended;
  onExporTicket: (trackingCode: string) => void;
  onExportTicketMl: (id: string) => void;
  loadingExportTicket: boolean;
  loadingExportTicketMl: boolean;
}

const DashboardVariantV2CarrierContentInWarehouseTabDataTableActionsMenu = ({
  data,
  onExporTicket,
  onExportTicketMl,
  loadingExportTicket,
  loadingExportTicketMl,
}: DashboardVariantV2CarrierContentInWarehouseTabDataTableActionsMenuProps): ReactElement => {
  const navigate = useNavigate();
  const { user } = useAppSelector(selectAuth);

  const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);
  const isUserOnlyCreatorOrder = useMemo(
    () => isOnlyCreatorOrder(user),
    [user],
  );

  const {
    dialogs: {
      detailsOrder: { handleOpen: handleOpenDialogDetailsOrder },
      changeStateOrder: { handleOpen: handleOpenDialogChangeStateOrder },
      modifyAssignmentOrder: {
        handleOpen: handleOpenDialogModifyAssignmentOrder,
      },
      cancelOrder: { handleOpen: handleOpenDialogCancelOrder },
      confirmDeleteOrder: { handleOpen: handleOpenConfirmDeleteOrder },
    },
  } = useDashboardVariantV2CarrierContentInWarehouseTabContext();

  const handleRedirectToNewOrder = useCallback(
    (extraData?: Record<string, unknown>) => {
      navigate(PATHS.ORDERS.NEW, { state: extraData });
    },
    [navigate],
  );

  const hasCarrier = useMemo(
    () => !isNull(data.carrierCompanyId),
    [data.carrierCompanyId],
  );

  // const isOwnItem = useMemo(
  //   () => isNull(data.carrierCompanyId),
  //   [data.carrierCompanyId],
  // );

  // const isOwnCompany = useMemo(
  //   () =>
  //     isCurrentLoggedCompany(data.carrierCompanyId, user?.companyId ?? null),
  //   [data.carrierCompanyId, user?.companyId],
  // );

  // const enabledShowRoute = useMemo(() => {
  //   if (!isOnAgendaOrder(data)) return false;

  //   if (!data.routeCode) return false;

  //   return !isUserOnlyCreatorOrder && (isOwnItem || isOwnCompany);
  // }, [data, isUserOnlyCreatorOrder, isOwnItem, isOwnCompany]);

  // const pathShowRoute = useMemo(
  //   () =>
  //     replaceParamsPath(PATHS.ROUTES.VEHICLE_PLANNING, {
  //       ':apiTransactionId': data.apiTransactionId as string,
  //       ':routeId': data.routeId as string,
  //     }),
  //   [data.apiTransactionId, data.routeId],
  // );

  const enabledEdit = useMemo(
    () => !isUserOnlyReader && !isUserOnlyCreatorOrder && data.canModify,
    [isUserOnlyReader, isUserOnlyCreatorOrder, data.canModify],
  );

  const enabledExportTicketMl = useMemo(
    () => !isUserOnlyReader && data.sourceImportTypeId === 5,
    [data.sourceImportTypeId, isUserOnlyReader],
  );

  const enabledDeleted = useMemo(() => {
    // if (isOnAgendaOrder(data)) return false;

    return (
      !isUserOnlyReader &&
      data.canDelete &&
      (!hasCarrier ||
        validatePartnerItemCollectStateType(data.collectItemStateTypeId, [
          CollectItemStateTypes.ToValidate,
          CollectItemStateTypes.ToCollect,
          CollectItemStateTypes.NotValidated,
        ]))
    );
  }, [data, hasCarrier, isUserOnlyReader]);

  const enabledCancel = useMemo(() => {
    // if (isOnAgendaOrder(data)) return false;

    return (
      !isUserOnlyReader &&
      (hasCarrier ||
        validatePartnerItemCollectStateType(data.collectItemStateTypeId, [
          CollectItemStateTypes.ToValidate,
          CollectItemStateTypes.ToCollect,
          CollectItemStateTypes.NotValidated,
        ]))
    );
  }, [data, hasCarrier, isUserOnlyReader]);

  return (
    <MenuActionsDataTable
      resource={{
        path: PATHS.ORDERS.BASE,
        id: data[ITEM_KEY_ID],
      }}
      items={{
        show: {
          enabled: true,
          onClick: () =>
            handleOpenDialogDetailsOrder({
              orderId: data[ITEM_KEY_ID],
              currentTab: null,
            }),
          divider: !enabledEdit,
        },
        edit: {
          enabled: enabledEdit,
          divider: true,
        },
      }}
      extraItems={[
        {
          enabled: !isUserOnlyReader,
          label: 'Cambiar estado',
          Icon: <ChangeStateIcon color="success" />,
          onClick: () =>
            handleOpenDialogChangeStateOrder({
              itemsIds: [data[ITEM_KEY_ID]],
              excludeStateOption:
                data.collectItemStateTypeId !== null
                  ? undefined
                  : data.itemStateTypeId,
            }),
        },
        {
          enabled: !isUserOnlyReader,
          label: 'Modificar asignación',
          Icon: <ModifyAssignmentIcon color="success" />,
          onClick: () =>
            handleOpenDialogModifyAssignmentOrder(data[ITEM_KEY_ID]),
          divider: true,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.LimitNotPro,
          },
        },
        // {
        //   enabled: enabledShowRoute,
        //   label: 'Ver ruta',
        //   Icon: <ShowIcon />,
        //   resource: { path: pathShowRoute },
        // },
        {
          enabled: true,
          label: 'Ver seguimiento de órden',
          Icon: <FontAwesomeIcon icon={RouteIcon} />,
          onClick: () => {
            openUrlInNewTab(getUrlTrackingOrder(data.trackingCode));
          },
          divider: true,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.TrackingPortalModule,
          },
        },
        {
          enabled: true,
          label: 'Exportar etiqueta',
          Icon: loadingExportTicket ? (
            <CircularProgress color="primary" disableShrink size={20} />
          ) : (
            <FontAwesomeIcon icon={BardCodeIcon} />
          ),
          onClick: () => onExporTicket(data.trackingCode),
          divider:
            enabledEdit ||
            enabledDeleted ||
            enabledCancel ||
            enabledExportTicketMl,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.MgmGenerateTickets,
            availableToDate: getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmGenerateTickets,
            ),
          },
        },
        {
          enabled: enabledExportTicketMl,
          label: 'Exportar etiqueta de Mercado Libre',
          Icon: loadingExportTicketMl ? (
            <CircularProgress color="primary" disableShrink size={20} />
          ) : (
            <FontAwesomeIcon icon={BardCodeIcon} />
          ),
          onClick: () => onExportTicketMl(data[ITEM_KEY_ID]),
          divider: enabledEdit || enabledDeleted || enabledCancel,
          validSubscriptionFeature: {
            validFeature: PlanFeaturesTypes.MgmGenerateTickets,
            availableToDate: getFeatureAvailableToDate(
              PlanFeaturesTypes.MgmGenerateTickets,
            ),
          },
        },
        {
          enabled: enabledEdit,
          label: 'Duplicar',
          Icon: <FontAwesomeIcon icon={DuplicateIcon} />,
          onClick: () =>
            handleRedirectToNewOrder({ duplicateById: data[ITEM_KEY_ID] }),
          divider: enabledCancel || enabledDeleted,
        },
        {
          enabled: enabledCancel,
          label: 'Desasignar colecta',
          Icon: <UnLinkIcon color="error" />,
          onClick: () => handleOpenDialogCancelOrder(data),
        },
        {
          enabled: enabledDeleted,
          label: 'Eliminar',
          Icon: <DeleteIcon color="error" />,
          onClick: () => handleOpenConfirmDeleteOrder(data),
        },
      ]}
    />
  );
};

export default memo(
  DashboardVariantV2CarrierContentInWarehouseTabDataTableActionsMenu,
);
